// Lote E: estado de PO 9, PO 10, PO 12 y PO 13 del Anexo 1 desde Incidentes. Lógica pura, sin base de datos.
// Reglas DECIDIDAS POR CLAUDE (31/10/2026), por validar con Kevin:
//  - Casos: accidentes (accidente de trabajo, de tránsito y en acto de servicio; el "incidente" o casi accidente no
//    se avisa al IESS) para PO 9 y PO 10; presunciones de enfermedad profesional para PO 12 y PO 13. Periodo evaluado:
//    los últimos 12 meses (fecha del caso entre hoy menos 12 meses y hoy).
//  - PO 9 y PO 12 (aviso al IESS, Resolución C.D. 513): sin casos → No aplica; algún caso vencido sin constancia →
//    No cumple; algún caso dentro del término sin constancia → Pendiente; todos con constancia → Cumple, con la
//    observación "aviso fuera de término" si alguno se envió después de la fecha límite.
//  - PO 10 y PO 13 (medidas correctivas): sin casos → No aplica. Plazo sugerido por StorSafe (no normativo) para
//    definir medidas: 30 días calendario desde la fecha del siniestro o de los indicios. Por caso: una medida abierta
//    con la fecha compromiso vencida → No cumple (en cualquier momento); sin ninguna medida, o con medidas abiertas
//    dentro de su fecha compromiso (o sin fecha), → En proceso durante esos 30 días; pasados los 30 días, sin ninguna
//    medida o con una abierta sin fecha compromiso → No cumple, y con medidas abiertas dentro de su fecha → Cumple
//    (medidas definidas y en plazo). Todas cerradas con evidencia → Cumple. El ítem toma el peor caso (No cumple, luego
//    En proceso, luego Cumple). Una medida "cerrada" sin evidencia (anterior al lote E) cuenta como abierta.
// Los textos de estas funciones los ve el cliente (Checklist y PDF): sin rutas, nombres ni códigos internos.

import type { Traza } from '../pe/pe-tipos';
import { plural } from '../plurales';
import { fechaLimiteAviso, restarMeses, sumarDias } from './inc-plazos';

export const MESES_PERIODO = 12;
export const TIPOS_ACCIDENTE = ['accidente_trabajo', 'accidente_transito', 'accidente_acto_servicio'] as const;
export const TIPO_ENFERMEDAD = 'enfermedad_profesional';
export type TipoCaso = 'accidente' | 'enfermedad';

export const ITEMS_INCIDENTES_LOTE_E = ['PO-09', 'PO-10', 'PO-12', 'PO-13'] as const;
export type ItemIncidentes = typeof ITEMS_INCIDENTES_LOTE_E[number];
// 'en_proceso' es el mismo estado (y la misma etiqueta "En proceso" del Checklist) que usan el plan, las inspecciones y las firmas.
export type EstadoCalculoInc = 'cumple' | 'en_proceso' | 'no_cumple' | 'pendiente' | 'no_aplica';

export const DIAS_PLAZO_MEDIDAS = 30;
export const NOTA_PLAZO_MEDIDAS = 'Plazo sugerido por StorSafe para definir medidas: 30 días (no normativo), contados desde la fecha del siniestro o de los indicios.';
export const finPlazoMedidas = (fechaCaso: string) => sumarDias(fechaCaso, DIAS_PLAZO_MEDIDAS);

export const NORMA_AVISO = 'Resolución C.D. 513 del IESS (Reglamento del Seguro General de Riesgos del Trabajo)';
export const NORMA_MEDIDAS = 'Criterio técnico de StorSafe (por validar)';
export const FUENTE_ANEXO1_INC = 'Anexo 1, Lista de verificación de cumplimiento de obligaciones de seguridad y salud en el trabajo (Ministerio del Trabajo)';

export interface CasoInc {
  id: string;
  incident_type: string;
  incident_date: string;
  iess_report_number?: string | null;
  iess_report_date?: string | null;
  iess_constancia_path?: string | null;
}

export interface MedidaInc {
  id: string;
  incident_id: string;
  status: string | null;
  due_date: string | null;
  evidence_path: string | null;
}

export function tipoCaso(incidentType: string): TipoCaso | null {
  if ((TIPOS_ACCIDENTE as readonly string[]).includes(incidentType)) return 'accidente';
  if (incidentType === TIPO_ENFERMEDAD) return 'enfermedad';
  return null;
}

export const requiereAviso = (incidentType: string) => tipoCaso(incidentType) !== null;

/** Casos del tipo dentro del periodo (últimos 12 meses hasta hoy). */
export function casosDelPeriodo<T extends CasoInc>(casos: T[], tipo: TipoCaso, hoy: string): T[] {
  const desde = restarMeses(hoy, MESES_PERIODO);
  return casos.filter(c => tipoCaso(c.incident_type) === tipo && c.incident_date >= desde && c.incident_date <= hoy);
}

// ── Aviso por caso ──────────────────────────────────────────────────────────

export type EstadoAviso = 'con_constancia' | 'fuera_de_termino' | 'en_termino' | 'vencido';
export const ETIQUETA_AVISO: Record<EstadoAviso, string> = {
  con_constancia: 'Aviso con constancia',
  fuera_de_termino: 'Aviso fuera de término',
  en_termino: 'Sin aviso, dentro del término',
  vencido: 'Vencido sin aviso',
};

export const tieneConstancia = (c: Pick<CasoInc, 'iess_constancia_path'>) => !!c.iess_constancia_path?.trim();

export interface AvisoCaso { estado: EstadoAviso; fechaLimite: string; enviado: string | null }

export function estadoAvisoCaso(c: CasoInc, hoy: string): AvisoCaso {
  const fechaLimite = fechaLimiteAviso(c.incident_date);
  if (tieneConstancia(c)) {
    const enviado = c.iess_report_date ?? null;
    return { estado: enviado && enviado > fechaLimite ? 'fuera_de_termino' : 'con_constancia', fechaLimite, enviado };
  }
  return { estado: hoy <= fechaLimite ? 'en_termino' : 'vencido', fechaLimite, enviado: null };
}

// ── Medidas por caso ────────────────────────────────────────────────────────

export type EstadoMedida = 'cerrada' | 'abierta' | 'vencida' | 'sin_fecha';
export const ETIQUETA_MEDIDA: Record<EstadoMedida, string> = {
  cerrada: 'Cerrada con evidencia', abierta: 'Abierta, en plazo', vencida: 'Vencida sin cierre', sin_fecha: 'Sin fecha compromiso',
};
export const ESTADO_CERRADA = 'completada';

export const cerradaConEvidencia = (m: Pick<MedidaInc, 'status' | 'evidence_path'>) => m.status === ESTADO_CERRADA && !!m.evidence_path?.trim();

export function estadoMedida(m: MedidaInc, hoy: string): EstadoMedida {
  if (cerradaConEvidencia(m)) return 'cerrada';
  if (!m.due_date) return 'sin_fecha';
  return m.due_date < hoy ? 'vencida' : 'abierta';
}

// ── Resultado por ítem ──────────────────────────────────────────────────────

export interface ResultadoItemInc {
  estado: EstadoCalculoInc;
  resumen: string;
  trazas: Traza[];
  observacion: string | null;   // p. ej. "aviso fuera de término" (el ítem queda en Cumple)
  casos: number;
}

const estadoTraza = (e: EstadoCalculoInc): Traza['estado'] =>
  e === 'cumple' ? 'cumple' : e === 'no_cumple' ? 'no_cumple' : e === 'no_aplica' ? 'informativo' : 'por_validar';

const NOMBRES: Record<TipoCaso, { uno: string; varios: string; ninguno: string }> = {
  accidente: { uno: 'accidente', varios: 'accidentes', ninguno: 'No hubo accidentes de trabajo en los últimos 12 meses.' },
  enfermedad: { uno: 'presunción de enfermedad profesional', varios: 'presunciones de enfermedad profesional',
    ninguno: 'No hubo presunciones de enfermedad profesional en los últimos 12 meses.' },
};

export const TIPO_DEL_ITEM: Record<ItemIncidentes, TipoCaso> = { 'PO-09': 'accidente', 'PO-10': 'accidente', 'PO-12': 'enfermedad', 'PO-13': 'enfermedad' };

export function resultadoAviso(item: 'PO-09' | 'PO-12', casos: CasoInc[], hoy: string): ResultadoItemInc {
  const tipo = TIPO_DEL_ITEM[item];
  const n = NOMBRES[tipo];
  const delPeriodo = casosDelPeriodo(casos, tipo, hoy);
  const avisos = delPeriodo.map(c => estadoAvisoCaso(c, hoy).estado);
  const cuenta = (e: EstadoAviso) => avisos.filter(a => a === e).length;
  const vencidos = cuenta('vencido'), enTermino = cuenta('en_termino'), fuera = cuenta('fuera_de_termino');
  const conConstancia = cuenta('con_constancia') + fuera;
  let estado: EstadoCalculoInc;
  let resumen: string;
  let observacion: string | null = null;
  if (delPeriodo.length === 0) {
    estado = 'no_aplica';
    resumen = n.ninguno;
  } else if (vencidos > 0) {
    estado = 'no_cumple';
    resumen = `${plural(vencidos, `${n.uno} con el término vencido`, `${n.varios} con el término vencido`)} y sin constancia del aviso al IESS.`;
  } else if (enTermino > 0) {
    estado = 'pendiente';
    resumen = `${plural(enTermino, `${n.uno} dentro del término`, `${n.varios} dentro del término`)}, todavía sin constancia del aviso al IESS.`;
  } else {
    estado = 'cumple';
    resumen = `${plural(conConstancia, `${n.uno} con la constancia del aviso al IESS`, `${n.varios} con la constancia del aviso al IESS`)}.`;
    if (fuera > 0) {
      observacion = 'aviso fuera de término';
      resumen += ` Observación: ${plural(fuera, 'aviso fuera de término', 'avisos fuera de término')}.`;
    }
  }
  const traza: Traza = {
    metodo: 'incidentes_aviso', norma: NORMA_AVISO,
    articulo_o_tabla: tipo === 'accidente' ? 'Aviso del accidente de trabajo en el término de 10 días hábiles'
      : 'Aviso de la presunción de enfermedad profesional en el término de 10 días hábiles',
    umbral: delPeriodo.length, valor: conConstancia, unidad: 'casos con constancia', estado: estadoTraza(estado),
    fuente_doc: `${FUENTE_ANEXO1_INC}, ${item.replace('-0', ' ').replace('-', ' ')}`, nota: resumen,
  };
  return { estado, resumen, trazas: [traza], observacion, casos: delPeriodo.length };
}

export type EstadoMedidasCaso = 'cumple' | 'en_proceso' | 'no_cumple';

/** Estado de las medidas de un caso (regla arriba). */
export function estadoMedidasCaso(c: CasoInc, medidas: MedidaInc[], hoy: string): { estado: EstadoMedidasCaso; finPlazo: string } {
  const finPlazo = finPlazoMedidas(c.incident_date);
  const enPlazo = hoy <= finPlazo;
  const estados = medidas.filter(m => m.incident_id === c.id).map(m => estadoMedida(m, hoy));
  if (estados.includes('vencida')) return { estado: 'no_cumple', finPlazo };
  if (estados.length > 0 && estados.every(e => e === 'cerrada')) return { estado: 'cumple', finPlazo };
  if (enPlazo) return { estado: 'en_proceso', finPlazo };
  if (estados.length === 0 || estados.includes('sin_fecha')) return { estado: 'no_cumple', finPlazo };
  return { estado: 'cumple', finPlazo };
}

export function resultadoMedidas(item: 'PO-10' | 'PO-13', casos: CasoInc[], medidas: MedidaInc[], hoy: string): ResultadoItemInc {
  const tipo = TIPO_DEL_ITEM[item];
  const n = NOMBRES[tipo];
  const delPeriodo = casosDelPeriodo(casos, tipo, hoy);
  const ids = new Set(delPeriodo.map(c => c.id));
  const suyas = medidas.filter(m => ids.has(m.incident_id));
  const porCaso = delPeriodo.map(c => ({ c, e: estadoMedidasCaso(c, suyas, hoy), tiene: suyas.some(m => m.incident_id === c.id) }));
  const estados = suyas.map(m => estadoMedida(m, hoy));
  const vencidas = estados.filter(e => e === 'vencida').length;
  const cerradas = estados.filter(e => e === 'cerrada').length;
  const sinMedidaFuera = porCaso.filter(x => !x.tiene && x.e.estado === 'no_cumple').length;
  const sinFechaFuera = porCaso.filter(x => x.tiene && x.e.estado === 'no_cumple'
    && !suyas.some(m => m.incident_id === x.c.id && estadoMedida(m, hoy) === 'vencida')).length;
  const enProceso = porCaso.filter(x => x.e.estado === 'en_proceso').length;
  let estado: EstadoCalculoInc;
  let resumen: string;
  if (delPeriodo.length === 0) {
    estado = 'no_aplica';
    resumen = n.ninguno;
  } else if (porCaso.some(x => x.e.estado === 'no_cumple')) {
    estado = 'no_cumple';
    const partes: string[] = [];
    if (sinMedidaFuera > 0) partes.push(plural(sinMedidaFuera, `${n.uno} sin ninguna medida correctiva pasados 30 días`, `${n.varios} sin ninguna medida correctiva pasados 30 días`));
    if (vencidas > 0) partes.push(plural(vencidas, 'medida vencida sin cierre', 'medidas vencidas sin cierre'));
    if (sinFechaFuera > 0) partes.push(plural(sinFechaFuera, `${n.uno} con medidas sin fecha compromiso pasados 30 días`, `${n.varios} con medidas sin fecha compromiso pasados 30 días`));
    resumen = `${partes.join('; ')}.`;
  } else if (enProceso > 0) {
    estado = 'en_proceso';
    resumen = `${plural(enProceso, `${n.uno} dentro del plazo de 30 días para definir y cerrar sus medidas`, `${n.varios} dentro del plazo de 30 días para definir y cerrar sus medidas`)}; `
      + `${plural(suyas.length, 'medida registrada', 'medidas registradas')}, ${cerradas} cerradas con evidencia.`;
  } else {
    estado = 'cumple';
    resumen = `${plural(suyas.length, 'medida correctiva', 'medidas correctivas')} en ${plural(delPeriodo.length, n.uno, n.varios)}: `
      + `${cerradas} cerradas con evidencia y ${suyas.length - cerradas} dentro de su fecha compromiso.`;
  }
  const traza: Traza = {
    metodo: 'incidentes_medidas', norma: NORMA_MEDIDAS,
    articulo_o_tabla: tipo === 'accidente' ? 'Medidas de control o correctivas para evitar nuevos accidentes'
      : 'Medidas de control o correctivas para evitar nuevos casos de enfermedad profesional',
    umbral: delPeriodo.length, valor: porCaso.filter(x => x.tiene).length, unidad: 'casos con medidas', estado: estadoTraza(estado),
    fuente_doc: `${FUENTE_ANEXO1_INC}, ${item.replace('-0', ' ').replace('-', ' ')}`, nota: `${resumen} ${NOTA_PLAZO_MEDIDAS}`,
  };
  return { estado, resumen, trazas: [traza], observacion: null, casos: delPeriodo.length };
}

export function resultadosIncidentes(casos: CasoInc[], medidas: MedidaInc[], hoy: string): Record<ItemIncidentes, ResultadoItemInc> {
  return {
    'PO-09': resultadoAviso('PO-09', casos, hoy),
    'PO-10': resultadoMedidas('PO-10', casos, medidas, hoy),
    'PO-12': resultadoAviso('PO-12', casos, hoy),
    'PO-13': resultadoMedidas('PO-13', casos, medidas, hoy),
  };
}

// ── Alertas en pantalla ────────────────────────────────────────────────────

export interface AlertaAviso { id: string; tipo: TipoCaso; fecha: string; fechaLimite: string; estado: 'en_termino' | 'vencido' }

/** Casos sin constancia (de cualquier fecha), los vencidos primero y después por fecha límite. */
export function alertasAviso(casos: CasoInc[], hoy: string): AlertaAviso[] {
  return casos.flatMap(c => {
    const tipo = tipoCaso(c.incident_type);
    if (!tipo) return [];
    const a = estadoAvisoCaso(c, hoy);
    if (a.estado !== 'en_termino' && a.estado !== 'vencido') return [];
    return [{ id: c.id, tipo, fecha: c.incident_date, fechaLimite: a.fechaLimite, estado: a.estado }];
  }).sort((x, y) => (x.estado === y.estado ? x.fechaLimite.localeCompare(y.fechaLimite) : x.estado === 'vencido' ? -1 : 1));
}
